'use client';
import React, { useState, useEffect } from 'react';
import { X, Link2, Copy, CheckCircle2, AlertCircle, Loader2, Send, Eye, Clock, UserCheck, UserX } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ShareProgramModalProps {
  athleteId: string;
  athleteName: string;
  athleteEmail: string;
  coachId: string;
  programId?: string;
  programTitle?: string;
  onClose: () => void;
}

type InviteStatus = 'not_invited' | 'invited' | 'pending_confirmation' | 'accepted' | 'active' | 'expired';

const STATUS_META: Record<InviteStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  not_invited:          { label: 'Not Invited',    color: '#71717a', bg: 'rgba(113,113,122,0.1)', icon: UserX },
  invited:              { label: 'Invited',         color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', icon: Send },
  pending_confirmation: { label: 'Email Pending',   color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', icon: Clock },
  accepted:             { label: 'Accepted',        color: '#10B981', bg: 'rgba(16,185,129,0.1)', icon: CheckCircle2 },
  active:               { label: 'Active',          color: '#10B981', bg: 'rgba(16,185,129,0.1)', icon: UserCheck },
  expired:              { label: 'Expired',         color: '#EF4444', bg: 'rgba(239,68,68,0.1)',  icon: AlertCircle },
};

export default function ShareProgramModal({
  athleteId,
  athleteName,
  athleteEmail,
  coachId,
  programId,
  programTitle,
  onClose,
}: ShareProgramModalProps) {
  const [inviteStatus, setInviteStatus] = useState<InviteStatus>('not_invited');
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteId, setInviteId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  useEffect(() => {
    loadExistingInvite();
  }, [athleteId]);

  const loadExistingInvite = async () => {
    const supabase = createClient();
    setLoading(true);
    try {
      const { data } = await supabase
        .from('athlete_invites')
        .select('id, token_hash, status, expires_at')
        .eq('athlete_id', athleteId)
        .eq('coach_id', coachId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (data) {
        const isExpired = new Date(data.expires_at) < new Date();
        const status: InviteStatus = isExpired && data.status === 'pending' ? 'expired' : (data.status as InviteStatus);
        setInviteStatus(status);
        setInviteToken(data.token_hash);
        setInviteId(data.id);
      }
    } catch {
      // No existing invite
    } finally {
      setLoading(false);
    }
  };

  const generateInvite = async () => {
    setGenerating(true);
    setError(null);
    const supabase = createClient();

    try {
      // Generate a secure random token
      const tokenBytes = new Uint8Array(32);
      crypto.getRandomValues(tokenBytes);
      const token = Array.from(tokenBytes).map(b => b.toString(16).padStart(2, '0')).join('');

      // Expire old invites for this athlete
      if (inviteId) {
        await supabase
          .from('athlete_invites')
          .update({ status: 'expired' })
          .eq('id', inviteId);
      }

      const { data, error: insertErr } = await supabase
        .from('athlete_invites')
        .insert({
          athlete_id: athleteId,
          coach_id: coachId,
          email: athleteEmail,
          token_hash: token,
          status: 'invited',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        })
        .select('id, token_hash')
        .single();

      if (insertErr) {
        throw new Error(insertErr.message);
      }

      // Update coach_athletes invite_status
      await supabase
        .from('coach_athletes')
        .update({ invite_status: 'invited' })
        .eq('athlete_id', athleteId)
        .eq('coach_id', coachId);

      setInviteToken(data.token_hash);
      setInviteId(data.id);
      setInviteStatus('invited');
    } catch (err: any) {
      setError(err.message ?? 'Failed to generate invite link.');
    } finally {
      setGenerating(false);
    }
  };

  const inviteLink = inviteToken ? `${siteUrl}/athlete/invite/${inviteToken}` : null;

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError('Failed to copy link. Please copy it manually.');
    }
  };

  const previewAthleteView = () => {
    window.open(`/athlete-dashboard`, '_blank');
  };

  const statusMeta = STATUS_META[inviteStatus];
  const StatusIcon = statusMeta.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: 'rgba(18,18,18,0.98)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(225,6,0,0.12)', border: '1px solid rgba(225,6,0,0.25)' }}
            >
              <Link2 size={15} style={{ color: '#E10600' }} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">Share Program with Athlete</h2>
              <p className="text-xs text-zinc-500">{athleteName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-200 transition-colors"
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            style={{ background: 'transparent' }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Athlete info */}
          <div
            className="rounded-xl p-4 space-y-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-zinc-500 mb-0.5">Athlete</p>
                <p className="text-zinc-200 font-medium">{athleteName}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-0.5">Email</p>
                <p className="text-zinc-200 font-medium truncate">{athleteEmail}</p>
              </div>
              {programTitle && (
                <div className="col-span-2">
                  <p className="text-xs text-zinc-500 mb-0.5">Program</p>
                  <p className="text-zinc-200 font-medium">{programTitle}</p>
                </div>
              )}
            </div>
          </div>

          {/* Invite Status */}
          <div
            className="rounded-xl p-4 flex items-center gap-3"
            style={{ background: statusMeta.bg, border: `1px solid ${statusMeta.color}30` }}
          >
            <StatusIcon size={18} style={{ color: statusMeta.color }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: statusMeta.color }}>
                Invite Status: {statusMeta.label}
              </p>
              <p className="text-xs text-zinc-500 mt-0.5">
                {inviteStatus === 'not_invited' && 'Generate a secure link to invite this athlete.'}
                {inviteStatus === 'invited' && 'Invite sent. Waiting for athlete to accept.'}
                {inviteStatus === 'pending_confirmation' && 'Athlete signed up. Waiting for email confirmation.'}
                {inviteStatus === 'accepted' && 'Athlete has accepted the invite and created their account.'}
                {inviteStatus === 'active' && 'Athlete is active and connected to their profile.'}
                {inviteStatus === 'expired' && 'Invite expired. Generate a new link.'}
              </p>
            </div>
          </div>

          {/* Invite link display */}
          {inviteLink && inviteStatus !== 'expired' && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-zinc-400 uppercase tracking-widest">Athlete Access Link</p>
              <div
                className="flex items-center gap-2 p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <p className="flex-1 text-xs text-zinc-400 font-mono truncate">{inviteLink}</p>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold flex-shrink-0 transition-all duration-150"
                  style={copied ? {
                    background: 'rgba(16,185,129,0.1)',
                    border: '1px solid rgba(16,185,129,0.3)',
                    color: '#10B981',
                  } : {
                    background: 'rgba(225,6,0,0.1)',
                    border: '1px solid rgba(225,6,0,0.3)',
                    color: '#FF3B30',
                  }}
                >
                  {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <p className="text-xs text-zinc-600">
                Link expires in 7 days. Share this with {athleteName} via email or message.
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-950/40 border border-red-800/50">
              <AlertCircle size={14} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="space-y-2">
            <button
              onClick={generateInvite}
              disabled={generating || loading}
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: generating || loading ? 'rgba(225,6,0,0.3)' : 'linear-gradient(135deg, #E10600, #8B0000)',
                color: '#fff',
                cursor: generating || loading ? 'not-allowed' : 'pointer',
              }}
            >
              {generating ? (
                <><Loader2 size={15} className="animate-spin" /> Generating Link...</>
              ) : (
                <><Link2 size={15} />
                  {inviteToken && inviteStatus !== 'expired' ? 'Regenerate Invite Link' : 'Generate Invite Link'}
                </>
              )}
            </button>

            {inviteLink && inviteStatus !== 'expired' && (
              <>
                <button
                  onClick={copyLink}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#d4d4d8',
                  }}
                >
                  <Copy size={14} />
                  Copy Athlete Access Link
                </button>

                <button
                  onClick={previewAthleteView}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#d4d4d8',
                  }}
                >
                  <Eye size={14} />
                  Preview Athlete View
                </button>
              </>
            )}
          </div>

          <div
            className="rounded-xl p-4 space-y-2"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-widest">How it works</p>
            <ol className="space-y-1.5 text-xs text-zinc-500">
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold flex-shrink-0">1.</span>
                Generate a secure invite link for {athleteName}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold flex-shrink-0">2.</span>
                Share the link via email or message
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold flex-shrink-0">3.</span>
                Athlete signs up or logs in using their invite email
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold flex-shrink-0">4.</span>
                Their account links to their athlete profile automatically
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold flex-shrink-0">5.</span>
                Athlete accesses their dashboard with assigned program
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
